import { useEffect, useRef, useState } from 'react';
import {
  playMoveSound,
  playGameStart,
  playGameEnd,
  playDrawOffer,
  playDrawDecline,
  isMuted,
  toggleMuted,
  unlockAudio,
} from '../utils/soundEffects';

// Plays sound effects in response to game state arriving over Firebase.
// Because every client (mover, teammates, spectators) receives the same
// moveHistory and status, driving sounds off this shared state keeps everyone
// in sync without any client double-playing its own move.
export const useSoundEffects = (game) => {
  const [muted, setMutedState] = useState(isMuted());
  const moveCountRef = useRef(null);
  const gameIdRef = useRef(null);
  const statusRef = useRef(null);
  const drawOfferRef = useRef({ gameId: null, has: false });

  // Unlock the AudioContext on the first user gesture (browser autoplay policy).
  useEffect(() => {
    unlockAudio();
  }, []);

  // React to new moves: play the effect for the most recent move's SAN.
  useEffect(() => {
    if (!game?.id) return;

    // A brand-new game has no `moveHistory` field until the first move, so
    // treat a missing array as length 0.
    const count = game.moveHistory?.length ?? 0;

    // First time we see this specific game, record the baseline so existing
    // history (reloading or joining mid-game) doesn't replay every move. Keying
    // on the game id means the baseline is set the moment the game loads — even
    // before any moveHistory exists — so the very first move still sounds.
    if (gameIdRef.current !== game.id) {
      gameIdRef.current = game.id;
      moveCountRef.current = count;
      return;
    }

    if (count > moveCountRef.current) {
      const lastMove = game.moveHistory[count - 1];
      playMoveSound(lastMove?.notation);
    }
    moveCountRef.current = count;
  }, [game?.id, game?.moveHistory]);

  // React to status transitions: game start and game end.
  useEffect(() => {
    const status = game?.status;
    if (!status) return;

    const prev = statusRef.current;
    if (prev !== null && prev !== status) {
      if (status === 'in_progress') {
        playGameStart();
      } else if (status === 'completed') {
        // Plays for every ending — checkmate, resign, draw, timeout. On
        // checkmate this lands just after the move's check sample.
        playGameEnd();
      }
    }
    statusRef.current = status;
  }, [game?.status]);

  // React to draw-offer transitions: a chime when offered, a soft blip when
  // declined. Acceptance/resignation clear the offer too, but those flip status
  // to 'completed' and are covered by the game-end sound above — so the decline
  // blip only fires while the game is still in progress.
  useEffect(() => {
    if (!game?.id) return;
    const hasOffer = !!game.drawOffer;
    const prev = drawOfferRef.current;

    // New game (or first load): set baseline, don't replay an existing offer.
    if (prev.gameId !== game.id) {
      drawOfferRef.current = { gameId: game.id, has: hasOffer };
      return;
    }

    if (hasOffer && !prev.has) {
      playDrawOffer();
    } else if (!hasOffer && prev.has && game.status === 'in_progress') {
      playDrawDecline();
    }
    drawOfferRef.current = { gameId: game.id, has: hasOffer };
  }, [game?.id, game?.drawOffer, game?.status]);

  const toggle = () => {
    const next = toggleMuted();
    setMutedState(next);
  };

  return { muted, toggleSound: toggle };
};
